import React from 'react';
import { Task, PriorityOption } from '../../../../types/task';
import { ChevronDownIcon } from 'lucide-react';
import { Menu, MenuButton, MenuItem, MenuItems, Portal } from '@headlessui/react';
import { PriorityArrows } from '../../../../utils/MyTasks.utils';

interface PriorityDropdownProps {
  currentPriority: Task['priority'];
  onPriorityChange: (priority: Task['priority']) => void;
  disabled?: boolean;
  showLabel?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

// Colors/labels only — arrow count and color-per-priority now come from the
// shared PriorityArrows (utils/MyTasks.utils.tsx) so priority display is
// identical between My Tasks and Project task views.
const priorityConfig = {
  low: {
    color: 'text-green-500',
    label: 'Low',
  },
  medium: {
    color: 'text-amber-500',
    label: 'Medium',
  },
  high: {
    color: 'text-red-500',
    label: 'High',
  },
} as const;

type PriorityKey = keyof typeof priorityConfig;

// Generate priorityOptions from the config
const priorityOptions: PriorityOption[] = (
  Object.keys(priorityConfig) as PriorityKey[]
).map((key) => ({
  value: key,
  label: priorityConfig[key].label,
  color: priorityConfig[key].color,
  icon: '↑', // Using consistent icon since we're using PriorityArrows component
}));

const PriorityDropdown: React.FC<PriorityDropdownProps> = ({
  currentPriority,
  onPriorityChange,
  disabled = false,
  showLabel,
  size = 'md',
  className = '',
}) => {
  const normalizedPriority =
  (currentPriority?.toLowerCase() as PriorityKey) ?? 'medium';
  const currentPriorityOption =
  priorityOptions.find((p) => p.value === normalizedPriority) ||
  priorityOptions[1];

  const sizeClasses = {
    sm: 'px-1 py-1 text-sm',
    md: 'px-2 py-1 text-sm',
    lg: 'px-3 py-2 text-base',
  };

  const handlePrioritySelect = (priority: Task['priority']) => {
    onPriorityChange(priority);
  };

  return (
    <Menu as='div' className='relative block w-full text-left'>
      <MenuButton
        type='button'
        disabled={disabled}
        className={`flex items-center gap-2 ${sizeClasses[size]} hover:bg-primary-subtle rounded transition-colors ${currentPriorityOption.color} disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
      >
        <PriorityArrows
          priority={normalizedPriority}
          size={size}
        />
        {showLabel && <span>{currentPriorityOption.label}</span>}
        <ChevronDownIcon className='w-3 h-3' />
      </MenuButton>

      <Portal>
        <MenuItems
          transition
          anchor="bottom start"
          className='mt-2 z-[9999] w-48 rounded-lg bg-primary-variant border border-primary-subtle shadow-lg transition duration-100 ease-out focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75'
        >
          <div className='p-4'>
            {/* Header */}
            <div className='text-neutral-light text-sm font-medium mb-3'>
              Priority
            </div>
          

            {/* Priority Options */}
            <div className='space-y-1 mb-3'>
              {priorityOptions.map((option) => (
                <MenuItem key={option.value}>
                  {({ focus }) => (
                    <button
                      onClick={() => handlePrioritySelect(option.value)}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded text-sm font-medium transition-all ${
                        focus || option.value === normalizedPriority
                          ? 'bg-primary-subtle'
                          : 'hover:bg-primary-subtle'
                      }`}
                      title={option.label}
                    >
                      <PriorityArrows
                        priority={option.value}
                        size="md"
                      />
                      <span>{option.label}</span>
                    </button>
                  )}
                </MenuItem>
              ))}
            </div>

            {/* Add Button */}
            {/* <MenuItem>
              {({ focus }) => (
                <button
                  className={`flex items-center gap-2 px-3 py-1 text-xs font-medium text-neutral-medium transition-colors rounded w-full ${
                    focus
                      ? 'bg-primary-intemediate text-neutral-light'
                      : 'hover:text-neutral-light bg-primary-subtle hover:bg-primary-intemediate'
                  }`}
                >
                  <span className='text-sm'>+</span>
                  Add
                </button>
              )}
            </MenuItem> */}
          </div>
        </MenuItems>
      </Portal>
    </Menu>
  );
};

export default PriorityDropdown;
